import { initialValueCart } from "@/atoms/cart";
import { sumCartItems } from "@/lib/pricing";
import { Cart, CartItem } from "@/types";

export const CART_STORAGE_KEY = "cart-local";
export const CART_UPDATED_AT_KEY = "cart-local-updated-at";

export const CART_TTL_MS = 5 * 60 * 60 * 1000;

export const CART_TTL_CHECK_INTERVAL_MS = 60 * 1000;

/**
 * Persists only the cart lines. Everything else on `Cart` is derived
 * (`totalPrice`) or merchant data, so it is rebuilt on load instead of
 * being trusted from a stale snapshot. Callers keep their own try/catch
 * + log message, so errors propagate here.
 */
export function saveLocalCart(cart: { items: CartItem[] }): void {
  localStorage.setItem(CART_STORAGE_KEY, JSON.stringify({ items: cart.items }));
  touchCartTimestamp();
}

/**
 * Records "the cart was modified now". Lives in a sibling key instead of
 * inside the cart payload because that payload is shape-heterogeneous (see
 * saveLocalCart) and, in remote-cart mode, mutations don't pass through
 * saveLocalCart at all — the hooks touch this directly.
 */
export function touchCartTimestamp(): void {
  try {
    localStorage.setItem(CART_UPDATED_AT_KEY, String(Date.now()));
  } catch {
    return;
  }
}

/** Epoch millis of the last cart modification, or null when the bag was never stamped. */
export function readCartUpdatedAt(): number | null {
  try {
    const raw = localStorage.getItem(CART_UPDATED_AT_KEY);
    const updatedAt = raw === null ? NaN : Number(raw);
    return Number.isNaN(updatedAt) ? null : updatedAt;
  } catch {
    return null;
  }
}

/**
 * True when the stored bag must be discarded: idle past CART_TTL_MS, or never
 * stamped at all. A bag without a timestamp has an unknown age (it predates the
 * TTL, or the stamp write failed), and an unknown age is treated as too old:
 * it may hold prices and items the merchant changed weeks ago.
 */
export function isStoredCartExpired(): boolean {
  const updatedAt = readCartUpdatedAt();
  if (updatedAt === null) return true;
  return Date.now() - updatedAt > CART_TTL_MS;
}

/**
 * Loads the locally stored cart, falling back to the initial empty cart
 * when absent, corrupted, or expired (idle past CART_TTL_MS or never
 * stamped — discarded here so every hydration path enforces the TTL).
 * Only `items` is read from storage; `totalPrice` is recomputed from the
 * lines and any legacy field (`serviceArea`, `minimumPrice`, …) is dropped.
 */
export function loadLocalCart(): Cart {
  try {
    const storedCart = localStorage.getItem(CART_STORAGE_KEY);
    if (storedCart) {
      const parsed = JSON.parse(storedCart);
      const items: CartItem[] = Array.isArray(parsed?.items) ? parsed.items : [];
      if (items.length > 0 && isStoredCartExpired()) {
        clearLocalCart();
        return initialValueCart;
      }
      return { items, totalPrice: sumCartItems(items) };
    }
  } catch (error) {
    console.error("Erro ao carregar carrinho do localStorage:", error);
  }
  return initialValueCart;
}

/** Removes the locally stored cart and its modification timestamp. */
export function clearLocalCart(): void {
  try {
    localStorage.removeItem(CART_STORAGE_KEY);
    localStorage.removeItem(CART_UPDATED_AT_KEY);
  } catch (error) {
    console.error("Erro ao limpar carrinho do localStorage:", error);
  }
}
